import classNames from 'classnames';
import styles from './slide-down-card.module.css';
import { CSSProperties, RefObject, useEffect, useMemo, useRef, useState } from 'react';
import useOnFocusAny from '@/utils/use-on-focus-any';
import { mergeRefs } from '@/utils/merge-refs';
import useDimensionsObserver from '@/utils/use-dimensions-observer';

interface SlideDownCardProps {
  /**
   * `height` is the max height the content will grow to.
   *
   * `autoGrow` will take precedence over `height`. However, you can use `height` in conjunction with `animateOnInitialLoad={false}`
   * so that the height will be used before the first time `open` is set to true. This will prevent sliding on load
   * */
  height?: string;
  /**
   * `autoGrow` will auto calculate and real-time adjust `height` based on the child content height.
   * See notes on the height prop regarding precedence.
   * */
  autoGrow?: boolean;
  animateOnInitialLoad?: boolean;
  children: React.ReactNode;
  open: boolean;
  'data-component'?: string;
  ref?: RefObject<HTMLDivElement | null> | null;
  /** automatically sets open to true if any child element is focused - this includes tabbing into the component */
  autoOpenOnFocus?: boolean;
  autoCloseOnBlur?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  wrapperClassname?: string;
}

export default function SlideDownCard({
  height,
  children,
  open: isOpen,
  'data-component': dataComponent,
  ref,
  autoOpenOnFocus,
  autoCloseOnBlur,
  onOpen,
  onClose,
  wrapperClassname,
  autoGrow,
  animateOnInitialLoad = true,
}: SlideDownCardProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const contentWrapperRef = useRef<HTMLDivElement | null>(null);
  const hasOpenedOnce = useRef(false);
  const isFirstLoad = useRef(true);

  const [open, setOpen] = useState(isOpen);

  const contentDimensions = useDimensionsObserver({
    ref: contentWrapperRef,
    customOptions: { pause: !autoGrow },
  });

  useEffect(() => {
    setTimeout(() => {
      setOpen(isOpen);
      isFirstLoad.current = false;
    }, 16);
  }, [isOpen]);

  const isFocused = useOnFocusAny({ ref: containerRef });
  useEffect(() => {
    if (isFocused && autoOpenOnFocus) {
      setTimeout(() => {
        setOpen(true);
      }, 16);
    }
    if (autoCloseOnBlur && !isFocused) {
      setTimeout(() => {
        setOpen(false);
      }, 16);
    }
  }, [autoCloseOnBlur, autoOpenOnFocus, isFocused]);

  useEffect(() => {
    if (open) {
      hasOpenedOnce.current = true;
      onOpen?.();
    } else {
      if (hasOpenedOnce.current) {
        onClose?.();
      }
    }
  }, [open, onOpen, onClose]);

  const cardHeight = useMemo(() => {
    const treatAsOpen = !animateOnInitialLoad && isFirstLoad.current && open;

    if (autoGrow) {
      if (contentDimensions && 'height' in contentDimensions) {
        if (open || treatAsOpen) {
          return `${contentDimensions.height}px`;
        }
        return '0px';
      }
    }

    if (open || treatAsOpen) {
      return height;
    }
    return '0px';
  }, [animateOnInitialLoad, autoGrow, contentDimensions, height, open]);

  return (
    <div
      data-component={dataComponent || 'SlideDownCard'}
      className={classNames(styles.slideDownCard, wrapperClassname, {
        [styles.open]: open || (isFirstLoad.current && !animateOnInitialLoad && open),
      })}
      style={
        {
          '--slide-down-card-height': cardHeight,
          ...(!animateOnInitialLoad && isFirstLoad.current
            ? {
                '--slide-down-card-transition-duration': '0s, 0s, 0s',
                '--slide-down-card-transition-delay': '0s, 0s, 0s',
              }
            : {}),
        } as CSSProperties
      }
      ref={mergeRefs(ref, containerRef)}
      /* note: inert: true disables focusing on both this div and any child elements */
      inert={autoOpenOnFocus ? undefined : !open}
    >
      <div style={{ height: autoGrow ? '100%' : height }} className={styles.animatedOuter}>
        <div
          ref={contentWrapperRef}
          style={{ height: autoGrow ? 'fit-content' : height }}
          className={styles.animatedInner}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
